import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import '../components/select-field.scss';
import { getManager, getManagerQuarters } from '../api/panel-api';

const ROMAN = ['I', 'II', 'III', 'IV'];

const ROWS = [
  { key: 'akb', label: 'АКБ', money: false },
  { key: 'sales', label: 'Продажи, ₽', money: true },
  { key: 'orders', label: 'Заказы', money: false },
  { key: 'orders_via_manager', label: 'Заказы через менеджера (ОЗМ)', money: false },
  { key: 'average_check', label: 'Средний чек, ₽', money: true },
  { key: 'comments', label: 'Комментарии', money: false },
];

function quarterIndex(start) {
  return Math.floor((Number(String(start).slice(5, 7)) - 1) / 3);
}

function menuLabel(start) {
  if (!start) return '';
  return `${ROMAN[quarterIndex(start)]} квартал – ${String(start).slice(0, 4)}`;
}

function buttonLabel(start) {
  return menuLabel(start).toUpperCase();
}

function axisLabel(start) {
  if (!start) return '';
  return `${ROMAN[quarterIndex(start)]} кв. ${String(start).slice(0, 4)}`;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatAmount(value) {
  const n = toNumber(value);
  if (n === null) return '0';
  return Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

function formatMoney(value) {
  return `${formatAmount(value)} ₽`;
}

function formatPercent(from, to) {
  const percent = Math.abs(((to - from) / from) * 100);
  return percent.toFixed(1);
}

function pairFromCard(card, key) {
  if (!card) return { selected: null, current: null };
  if (key === 'orders_via_manager') {
    return {
      selected: card.orders_via_manager && card.orders_via_manager.selected,
      current: card.orders_via_manager && card.orders_via_manager.current,
    };
  }
  if (key === 'comments') {
    return {
      selected: card.comments && card.comments.selected,
      current: card.comments && card.comments.current,
    };
  }
  const selected = card.metrics && card.metrics.selected;
  const current = card.metrics && card.metrics.current;
  return {
    selected: selected ? selected[key] : null,
    current: current ? current[key] : null,
  };
}

function quarterValue(item, key) {
  if (!item) return null;
  if (key === 'akb' || key === 'sales' || key === 'orders' || key === 'average_check') {
    return toNumber(item[key]);
  }
  return null;
}

function seriesValue(item, key, snap, currentStart) {
  const direct = quarterValue(item, key);
  if (direct !== null) return direct;
  if (!snap) return null;
  const pair = pairFromCard(snap, key);
  if (item.quarter_start === currentStart) return toNumber(pair.current);
  return toNumber(pair.selected);
}

function niceMax(max) {
  if (!(max > 0)) return 10;
  const mag = 10 ** Math.floor(Math.log10(max));
  const steps = [1, 2, 4, 5, 8, 10];
  const found = steps.map((step) => step * mag).find((value) => value >= max);
  return found || mag * 10;
}

function formatTick(value, money) {
  if (!money) return String(value);
  if (value >= 1000000) {
    const n = value / 1000000;
    return `${Number.isInteger(n) ? n : n} млн`;
  }
  if (value >= 1000) {
    const n = value / 1000;
    return `${Number.isInteger(n) ? n : n} тыс`;
  }
  return formatAmount(value);
}

function MetricDelta({ selected, current }) {
  const from = toNumber(selected);
  const to = toNumber(current);
  if (from === null || to === null || (from === 0 && to === 0)) return null;
  if (from === 0 && to > 0) {
    return <span className="metric-delta metric-delta--new">NEW</span>;
  }
  if (from === 0) return null;
  const percent = formatPercent(from, to);
  if (to === from) {
    return <span className="metric-delta">{percent} %</span>;
  }
  const up = to > from;
  return (
    <span className={up ? 'metric-delta metric-delta--up' : 'metric-delta metric-delta--down'}>
      <Icon name="trend-up" size={12} tone="inherit" />
      {percent} %
    </span>
  );
}

function QuarterSelect({ value, options, onChange }) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(event) {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    }
    function onKey(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuStyle({
      left: rect.left,
      top: rect.bottom + 4,
      minWidth: Math.max(rect.width, 180),
    });
    setOpen(true);
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="stats-quarter"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
      >
        {buttonLabel(value)}
        <Icon name="chevron-down" size={16} />
      </button>
      {open ? createPortal(
        <div ref={menuRef} className="select-field__menu stats-quarter__menu" role="listbox" style={menuStyle}>
          {options.map((start) => (
            <button
              key={start}
              type="button"
              className={start === value ? 'select-field__option is-selected' : 'select-field__option'}
              onClick={() => {
                onChange(start);
                setOpen(false);
              }}
            >
              {menuLabel(start)}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </>
  );
}

function LineChart({ title, points, money }) {
  const gradientId = useId().replace(/:/g, '');
  const svgRef = useRef(null);
  const [hover, setHover] = useState(-1);
  const width = 320;
  const height = 168;
  const left = 36;
  const right = 10;
  const top = 10;
  const bottom = 26;
  const plotW = width - left - right;
  const plotH = height - top - bottom;
  if (!points.length) return null;
  const values = points.map((item) => (item.value === null ? 0 : item.value));
  const max = niceMax(Math.max(0, ...values));
  const ticks = [0, max / 2, max];

  function xAt(index) {
    if (points.length <= 1) return left + plotW;
    return left + (index / (points.length - 1)) * plotW;
  }

  function yAt(value) {
    return top + plotH - (value / max) * plotH;
  }

  const coords = points.map((item, index) => ({
    ...item,
    x: xAt(index),
    y: yAt(item.value === null ? 0 : item.value),
  }));
  const line = coords.map((item, index) => `${index ? 'L' : 'M'}${item.x} ${item.y}`).join(' ');
  const startX = points.length <= 1 ? left : coords[0].x;
  const last = coords[coords.length - 1];
  const area = `${points.length <= 1 ? `M${left} ${coords[0].y} L${coords[0].x} ${coords[0].y}` : line} L${last.x} ${top + plotH} L${startX} ${top + plotH} Z`;
  const active = hover >= 0 ? hover : -1;

  function onMove(event) {
    const svg = svgRef.current;
    if (!svg || points.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    let nearest = 0;
    let best = Infinity;
    coords.forEach((item, index) => {
      const dist = Math.abs(item.x - x);
      if (dist < best) {
        best = dist;
        nearest = index;
      }
    });
    setHover(nearest);
  }

  const tip = active >= 0 ? coords[active] : null;
  let tipDelta = null;
  if (tip && active > 0 && points[active - 1].value !== null && tip.value !== null) {
    const from = points[active - 1].value;
    const to = tip.value;
    if (from === 0 && to === 0) {
      tipDelta = null;
    } else if (from === 0) {
      tipDelta = null;
    } else {
      const up = to > from;
      const sign = to === from ? '' : up ? '+ ' : '− ';
      const tone = to === from ? '' : up ? 'is-up' : 'is-down';
      tipDelta = { text: `${sign}${formatPercent(from, to)}% к прошлому кварталу`, tone };
    }
  }

  return (
    <article className="stats-chart">
      <h3>{title}</h3>
      <div className="stats-chart__plot">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={title}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(-1)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--color-status-info-fg)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="var(--color-status-info-fg)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={left}
                x2={width - right}
                y1={yAt(tick)}
                y2={yAt(tick)}
                className={tick === 0 ? 'stats-chart__axis' : 'stats-chart__grid'}
              />
              <text x={2} y={yAt(tick) + 3} className="stats-chart__tick">{formatTick(tick, money)}</text>
            </g>
          ))}
          <path d={area} fill={`url(#${gradientId})`} />
          <path d={points.length <= 1 ? `M${left} ${coords[0].y} L${coords[0].x} ${coords[0].y}` : line} className="stats-chart__line" />
          {coords.map((item, index) => {
            const on = index === active || (active < 0 && item.current);
            return (
              <g key={item.start || index}>
                {on ? <circle cx={item.x} cy={item.y} r="8" className="stats-chart__halo" /> : null}
                <circle cx={item.x} cy={item.y} r={on ? 5 : 3.5} className="stats-chart__dot" />
              </g>
            );
          })}
          {coords.map((item) => (
            <text
              key={`x-${item.start}`}
              x={item.x}
              y={height - 6}
              textAnchor="middle"
              className={item.current ? 'stats-chart__xlabel is-current' : 'stats-chart__xlabel'}
            >
              {item.label}
            </text>
          ))}
        </svg>
        {tip ? (
          <div
            className="stats-chart__tip"
            style={{ left: `${(tip.x / width) * 100}%`, top: `${(tip.y / height) * 100}%` }}
          >
            <strong>{money ? formatMoney(tip.value) : formatAmount(tip.value)}</strong>
            {tipDelta ? <span className={tipDelta.tone}>{tipDelta.text}</span> : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}

function ManagerStats({ managerId, manager }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [items, setItems] = useState([]);
  const [currentStart, setCurrentStart] = useState('');
  const [selectedStart, setSelectedStart] = useState('');
  const [snaps, setSnaps] = useState({});
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getManagerQuarters(managerId)
      .then(async (data) => {
        const list = (Array.isArray(data.items) ? data.items : [])
          .slice()
          .sort((a, b) => String(a.quarter_start).localeCompare(String(b.quarter_start)));
        const current = data.current_quarter_start || (manager.metrics && manager.metrics.current_quarter_start) || '';
        const completed = list.filter((item) => item.quarter_start !== current);
        const initial = completed.length ? completed[completed.length - 1].quarter_start : '';
        const nextSnaps = {};
        if (current) nextSnaps[current] = manager;
        const extra = completed.map((item) => item.quarter_start);
        const loaded = await Promise.all(extra.map((start) => (
          getManager(managerId, { quarter_start: start }).then((card) => [start, card])
        )));
        loaded.forEach(([start, card]) => {
          nextSnaps[start] = card;
        });
        if (cancelled) return;
        setItems(list);
        setCurrentStart(current);
        setSelectedStart(initial);
        setSnaps(nextSnaps);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setLoading(false);
        setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [manager, managerId, tick]);

  const completed = useMemo(
    () => items.filter((item) => item.quarter_start !== currentStart).map((item) => item.quarter_start),
    [items, currentStart],
  );
  const selectedCard = snaps[selectedStart] || null;
  const currentCard = snaps[currentStart] || manager;

  const charts = ROWS.map((row) => ({
    ...row,
    points: items.map((item) => ({
      start: item.quarter_start,
      label: axisLabel(item.quarter_start),
      value: seriesValue(item, row.key, snaps[item.quarter_start], currentStart),
      current: item.quarter_start === currentStart,
    })),
  }));

  return (
    <div className="manager-stats">
      <div className="manager-tab__head">
        <h2>Статистика</h2>
      </div>
      {loading ? (
        <div className="stats-status" role="status">
          <span className="stats-spinner" aria-hidden="true" />
          <p>Загружаем</p>
        </div>
      ) : error ? (
        <div className="empty managers-empty managers-empty--tab stats-empty">
          <div className="empty__icon"><Icon name="alert" size={24} tone="inherit" /></div>
          <div>
            <p className="empty__title">Данные не загрузились</p>
            <p className="empty__lead">Обновите — обычно помогает. Если повторится, напишите в ОДПР</p>
          </div>
          <button type="button" className="btn btn--secondary" onClick={() => setTick((value) => value + 1)}>
            Обновить
          </button>
        </div>
      ) : (
        <>
          <table className={completed.length ? 'stats-table' : 'stats-table stats-table--solo'}>
            <thead>
              <tr>
                <th>Показатель</th>
                {completed.length ? (
                  <th>
                    <QuarterSelect value={selectedStart} options={completed} onChange={setSelectedStart} />
                  </th>
                ) : null}
                <th>Текущий квартал</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const selected = completed.length ? pairFromCard(selectedCard, row.key).selected : null;
                const current = pairFromCard(currentCard, row.key).current;
                return (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    {completed.length ? <td>{formatAmount(selected)}</td> : null}
                    <td>
                      <span className="stats-table__now">
                        <span>{formatAmount(current)}</span>
                        {completed.length ? <MetricDelta selected={selected} current={current} /> : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <h2 className="stats-charts__title">Динамика по кварталам</h2>
          <div className="stats-charts">
            {charts.map((chart) => (
              <LineChart key={chart.key} title={chart.label} points={chart.points} money={chart.money} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ManagerStats;
